<template>
  <v-row :gap="[32, 24]">
    <v-col cols="12" md="6">
      <v-text-field
        v-model.trim="person.firstname"
        :rules="[required]"
        :error-messages="missingMessage('firstname')"
      >
        <template #label>
          {{ t("treezorOnboarding.effectiveBeneficiaries.firstname") }}
          <span class="text-error">*</span>
        </template>
      </v-text-field>
    </v-col>
    <v-col cols="12" md="6">
      <v-text-field
        v-model.trim="person.lastname"
        :rules="[required]"
        :error-messages="missingMessage('lastname')"
      >
        <template #label>
          {{ t("treezorOnboarding.effectiveBeneficiaries.lastname") }}
          <span class="text-error">*</span>
        </template>
      </v-text-field>
    </v-col>
    <v-col cols="12" md="6">
      <v-text-field
        v-model.trim="person.middleNames"
        :label="t('treezorOnboarding.effectiveBeneficiaries.middleNames')"
      />
    </v-col>
    <v-col cols="12" md="6">
      <v-text-field
        v-model="person.birthday"
        :error-messages="missingMessage('birthday')"
        type="date"
        :rules="[dateInPast]"
      >
        <template #label>
          {{ t("treezorOnboarding.effectiveBeneficiaries.birthday") }}
          <span class="text-error">*</span>
        </template>
      </v-text-field>
    </v-col>
    <v-col cols="12" md="6">
      <v-text-field
        v-model.trim="person.placeOfBirth"
        :error-messages="missingMessage('placeOfBirth')"
      >
        <template #label>
          {{ t("treezorOnboarding.effectiveBeneficiaries.placeOfBirth") }}
          <span class="text-error">*</span>
        </template>
      </v-text-field>
    </v-col>
    <v-col cols="12" md="6">
      <v-autocomplete
        v-model="person.birthCountry"
        autocomplete="birth-country"
        :error-messages="missingMessage('birthCountry')"
        :items="isoCountryItems"
        clearable
      >
        <template #label>
          {{ t("treezorOnboarding.effectiveBeneficiaries.birthCountry") }}
          <span class="text-error">*</span>
        </template>
      </v-autocomplete>
    </v-col>
    <v-col cols="12" md="6">
      <v-autocomplete
        v-model="person.nationality"
        autocomplete="nationality"
        :error-messages="missingMessage('nationality')"
        :items="isoCountryItems"
        clearable
      >
        <template #label>
          {{ t("treezorOnboarding.effectiveBeneficiaries.nationality") }}
          <span class="text-error">*</span>
        </template>
      </v-autocomplete>
    </v-col>
    <slot />
  </v-row>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { useRules } from "@/composables/useRules";
import type { TreezorKycEffectiveBeneficiaryField } from "@aircap/common/treezor";
import { useTreezorOnboarding } from "./useTreezorOnboarding";

const props = defineProps<{ missingFields: readonly TreezorKycEffectiveBeneficiaryField[] }>();
const person = defineModel<{
  firstname: string;
  lastname: string;
  middleNames: string;
  birthday: string;
  placeOfBirth: string;
  birthCountry: string | null;
  nationality: string | null;
}>({ required: true });

const { t } = useI18n();
const { dateInPast, required } = useRules();
const { isoCountryItems } = useTreezorOnboarding();

const missingMessage = (
  field: "firstname" | "lastname" | "birthday" | "placeOfBirth" | "birthCountry" | "nationality",
) =>
  props.missingFields.includes(field) && required(person.value[field]) !== true
    ? t("errors.required")
    : [];
</script>
